// System Configuration store - processes getSystemConfigs API and handles localStorage persistence

const systemConfigStore: Record<string, any> = {};

const SYSTEM_CONFIG_CACHE_KEY = "@system_config_key";

/**
 * Get system configuration value by key, or return the whole config object if no key specified
 * @param key Optional configuration key (e.g. "enable_stp_module")
 * @returns Config value or full dictionary
 */
export function getSystemConfig(key?: string): any {
  if (!key) return systemConfigStore;
  return systemConfigStore[key];
}

/**
 * Save system configuration to localStorage
 */
export function saveSystemConfigToStorage(): void {
  try {
    localStorage.setItem(SYSTEM_CONFIG_CACHE_KEY, JSON.stringify(systemConfigStore));
  } catch (error) {
    console.error("Error saving system config to storage:", error);
  }
}

/**
 * Load persisted system configuration from localStorage on startup
 */
export function loadPersistedSystemConfig(): boolean {
  try {
    const stored = localStorage.getItem(SYSTEM_CONFIG_CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.assign(systemConfigStore, parsed);
      return true;
    }
  } catch (error) {
    console.error("Error loading persisted system config:", error);
  }
  return false;
}

// Auto-load on module import
loadPersistedSystemConfig();

/**
 * Clear persisted system config (on logout)
 */
export function clearPersistedSystemConfig(): void {
  try {
    Object.keys(systemConfigStore).forEach((key) => delete systemConfigStore[key]);
    localStorage.removeItem(SYSTEM_CONFIG_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing system config:", error);
  }
}

/**
 * Process getSystemConfigs API response and convert into systemConfigStore map
 * API response structure contains: data.created, data.records, data.data, or data array
 */
export function updateSystemConfigFromAPI(response: any): Record<string, any> {
  if (!response) return systemConfigStore;

  let recordsList: any[] = [];

  const dataObj = response?.data?.data || response?.data || response;

  if (Array.isArray(dataObj?.created)) {
    recordsList = dataObj.created;
  } else if (Array.isArray(dataObj?.records)) {
    recordsList = dataObj.records;
  } else if (Array.isArray(dataObj?.data)) {
    recordsList = dataObj.data;
  } else if (Array.isArray(dataObj)) {
    recordsList = dataObj;
  }

  if (recordsList && recordsList.length > 0) {
    recordsList.forEach((item: any) => {
      if (!item || !item.ConfigKey) return;
      systemConfigStore[item.ConfigKey] = item.ConfigValue;
    });

    saveSystemConfigToStorage();
  }

  return systemConfigStore;
}
